import { useState } from "react";
import { Trash2, Plus, Users, RadioTower, Link2, Pencil, AlarmClock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import ChannelEditDialog, { CHANNEL_COLORS } from "./ChannelEditDialog";
import PatchDialog from "./PatchDialog";
import TalkgroupCard from "./TalkgroupCard";
import { TONES } from "../lib/tones";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { IDS } from "../constants/testIds";
import PTTButton from "./PTTButton";
import { Waveform } from "./Waveform";

export default function ChannelGrid({ channels, activeChannelId, txStatus, onPTTStart, onPTTEnd, onAdd, onDelete, onUpdate, onTone, toneActiveId, selectMode, onToggleSelectMode, selectedIds = [], onToggleSelect, onSelectAll, patchesByChannel = {}, patches = [], onAddPatch, onTogglePatch, onDeletePatch, talkgroups = [], contacts = [], activeTalkgroupId, onTalkgroupPTTStart, onDeleteTalkgroup, onDropOnTalkgroup, onRemoveMember }) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [color, setColor] = useState("#2563EB");
  const [editing, setEditing] = useState(null);

  const submit = async () => {
    if (!name.trim()) return;
    await onAdd({ name: name.trim(), description: desc, color });
    setName(""); setDesc(""); setOpen(false);
  };

  const colorMap = { "#2563EB": "blue", "#16A34A": "green", "#EA580C": "orange", "#0D9488": "teal", "#7C3AED": "violet", "#0F172A": "slate", "#CA8A04": "amber", "#DB2777": "pink", "#DC2626": "orange" };
  const sorted = [...channels].sort((a, b) => (a.priority === "high" ? 0 : 1) - (b.priority === "high" ? 0 : 1));

  return (
    <section data-testid={IDS.channels.grid} className="h-full flex flex-col gap-4 min-w-0">
      <ChannelEditDialog channel={editing} open={!!editing} onOpenChange={(o) => !o && setEditing(null)} onSave={onUpdate} />
      <div className="flex items-center justify-between flex-shrink-0">
        <div>
          <div className="overline">Channels · {channels.length}</div>
          <h2 className="text-xl font-semibold tracking-tight">Live Channels</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button
            data-testid={IDS.broadcast.toggle}
            size="sm"
            variant={selectMode ? "default" : "outline"}
            className={`gap-1.5 ${selectMode ? "bg-amber-500 hover:bg-amber-600 text-slate-900" : ""}`}
            onClick={onToggleSelectMode}
          >
            <RadioTower className="h-4 w-4" /> {selectMode ? "Broadcast mode" : "Multi-select"}
          </Button>
          {selectMode && channels.length > 0 && (
            <Button data-testid={IDS.broadcast.selectAll} size="sm" variant="ghost" onClick={onSelectAll}>
              {selectedIds.length === channels.length ? "None" : "All"}
            </Button>
          )}
          <PatchDialog patches={patches} channels={channels} onAdd={onAddPatch} onToggle={onTogglePatch} onDelete={onDeletePatch} />
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid={IDS.channels.addChannel} size="sm" className="gap-1.5">
              <Plus className="h-4 w-4" /> Add channel
            </Button>
          </DialogTrigger>
          <DialogContent data-testid={IDS.channels.addChannelDialog} className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Add channel</DialogTitle>
              <DialogDescription>Create a new dispatch channel with an accent color.</DialogDescription>
            </DialogHeader>
            <div className="grid gap-3 py-2">
              <div className="grid gap-1.5">
                <Label>Channel name</Label>
                <Input data-testid={IDS.channels.addChannelName} value={name} onChange={e => setName(e.target.value)} placeholder="Dispatch Bravo" />
              </div>
              <div className="grid gap-1.5">
                <Label>Description</Label>
                <Input value={desc} onChange={e => setDesc(e.target.value)} placeholder="Optional" />
              </div>
              <div className="grid gap-1.5">
                <Label>Accent</Label>
                <div className="flex gap-2">
                  {CHANNEL_COLORS.map(c => (
                    <button key={c} onClick={() => setColor(c)} className={`h-8 w-8 rounded-full border-2 ${color === c ? "border-slate-900" : "border-transparent"}`} style={{ background: c }} aria-label={c} />
                  ))}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button data-testid={IDS.channels.addChannelSubmit} onClick={submit}>Create channel</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {channels.length === 0 ? (
        <div className="rounded-xl border border-dashed border-slate-300 p-10 text-center text-slate-500 bg-white">
          <Users className="mx-auto h-8 w-8 mb-2 opacity-60" />
          No channels yet. Add your first channel to start dispatching.
        </div>
      ) : (
        <div className="flex-1 min-h-0 overflow-y-auto thin-scroll pr-1">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {sorted.map(ch => {
            const isActive = activeChannelId === ch.id;
            const status = isActive ? txStatus : "idle";
            const selected = selectedIds.includes(ch.id);
            const patches = patchesByChannel[ch.id] || [];
            const toneOn = toneActiveId === ch.id;
            return (
              <article
                key={ch.id}
                data-testid={IDS.channels.card(ch.id)}
                onClick={selectMode ? () => onToggleSelect(ch.id) : undefined}
                className={`group rounded-xl border bg-white p-5 flex flex-col gap-4 transition ${
                  selected ? "border-amber-400 ring-2 ring-amber-200 shadow-sm" : "border-slate-200 hover:border-slate-300 hover:shadow-sm"
                } ${selectMode ? "cursor-pointer" : ""}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-3 min-w-0">
                    {selectMode && (
                      <Checkbox
                        data-testid={IDS.broadcast.channelCheck(ch.id)}
                        checked={selected}
                        onCheckedChange={() => onToggleSelect(ch.id)}
                        onClick={e => e.stopPropagation()}
                        className="mt-1 h-5 w-5 data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                      />
                    )}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: ch.color }} />
                        <span className="overline">Channel</span>
                        {ch.priority === "high" && <span className="text-[10px] font-mono px-1.5 py-0.5 rounded bg-slate-900 text-white">PRIORITY</span>}
                        {patches.length > 0 && (
                          <span data-testid={IDS.patches.badge(ch.id)} title={patches.map(p => p.name).join(", ")} className="inline-flex items-center gap-1 text-[10px] font-mono px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">
                            <Link2 className="h-3 w-3" /> PATCHED{patches.length > 1 ? ` ×${patches.length}` : ""}
                          </span>
                        )}
                      </div>
                      <h3 className="font-semibold text-base tracking-tight truncate">{ch.name}</h3>
                      <p className="text-xs text-slate-500 truncate">{ch.description || "No description"}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                    <button
                      data-testid={IDS.channels.tone(ch.id)}
                      onClick={() => onTone(ch)}
                      disabled={toneOn || status === "transmitting"}
                      title={`Send ${TONES[ch.alert_tone || "two_tone"].label} alert tone`}
                      className={`h-8 w-8 grid place-items-center rounded-md border transition ${toneOn ? "bg-amber-500 border-amber-500 text-white animate-pulse" : "border-slate-200 text-slate-500 hover:text-amber-600 hover:border-amber-300 hover:bg-amber-50"} disabled:opacity-60`}
                      aria-label="Send alert tone"
                    >
                      <AlarmClock className="h-4 w-4" />
                    </button>
                    <button
                      data-testid={IDS.channels.edit(ch.id)}
                      onClick={() => setEditing(ch)}
                      className="h-8 w-8 grid place-items-center rounded-md border border-slate-200 text-slate-500 hover:text-slate-900 hover:bg-slate-50 transition"
                      aria-label="Edit channel"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      data-testid={IDS.channels.delete(ch.id)}
                      onClick={() => onDelete(ch.id)}
                      className="h-8 w-8 grid place-items-center rounded-md opacity-0 group-hover:opacity-100 transition text-slate-400 hover:text-red-600"
                      aria-label="Delete channel"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3" onClick={e => e.stopPropagation()}>
                  <div className="flex flex-col gap-1">
                    <span className="overline">
                      {toneOn ? "ALERT TONE" : status === "transmitting" ? "TRANSMITTING" : status === "receiving" ? "INCOMING" : "STANDBY"}
                    </span>
                    <Waveform
                      active={status !== "idle" || toneOn}
                      color={toneOn ? "text-amber-500" : status === "transmitting" ? "text-emerald-600" : status === "receiving" ? "text-blue-600" : "text-slate-300"}
                    />
                  </div>
                  <PTTButton
                    testId={IDS.channels.ptt(ch.id)}
                    label={`Push to talk on ${ch.name}`}
                    color={colorMap[ch.color] || "blue"}
                    status={status}
                    size="md"
                    onPressStart={() => onPTTStart(ch)}
                    onPressEnd={() => onPTTEnd(ch)}
                  />
                </div>
              </article>
            );
          })}
        </div>
        {talkgroups.length > 0 && (
          <div data-testid={IDS.talkgroups.section} className="mt-5">
            <div className="flex items-center gap-2 mb-2">
              <div className="overline">Talkgroups · {talkgroups.length}</div>
              <span className="text-[10px] text-slate-400">ad-hoc · private to members · drag a unit here to add</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {talkgroups.map(tg => (
                <TalkgroupCard key={tg.id} tg={tg} contacts={contacts} status={activeTalkgroupId === tg.id ? txStatus : "idle"} onPTTStart={() => onTalkgroupPTTStart(tg)} onPTTEnd={onPTTEnd} onDelete={() => onDeleteTalkgroup(tg.id)} onDropContact={(cid) => onDropOnTalkgroup(tg.id, cid)} onRemoveMember={(cid) => onRemoveMember(tg.id, cid)} />
              ))}
            </div>
          </div>
        )}
        </div>
      )}
    </section>
  );
}
